import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Images } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Skeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="rounded-3xl border border-black/10 bg-white h-80 animate-pulse" />
      ))}
    </div>
  );
}

export default function ShowcaseGallery() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    fetch(`${API}/gallery`)
      .then((r) => r.json())
      .then((d) => setItems(Array.isArray(d) ? d : []))
      .catch(() => setItems([]));
  }, []);

  return (
    <section id="work" className="relative py-20 sm:py-28 scroll-mt-20 border-t border-black/10 bg-royal-soft">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-3xl">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="font-mono text-[11px] sm:text-xs tracking-[0.3em] uppercase text-gold-dark"
          >
            Work Showcase
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.08 }}
            className="mt-4 font-display text-3xl sm:text-5xl font-bold tracking-tight text-royal-deep leading-[1.1]"
          >
            Moments we&rsquo;ve <span className="text-gold">made.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.16 }}
            className="mt-5 text-sm sm:text-lg text-black/70 leading-relaxed"
          >
            A look at the events, activations and brand moments delivered by the OEC team.
          </motion.p>
        </div>

        {items === null && <Skeleton />}

        {items !== null && items.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            data-testid="showcase-empty-state"
            className="mt-12 rounded-3xl border border-dashed border-black/20 bg-white/70 px-8 py-16 text-center"
          >
            <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-gold-soft border border-gold/50 text-gold-dark">
              <Images size={24} />
            </span>
            <h3 className="mt-6 font-display text-xl font-semibold text-royal-deep">
              Our showcase is being curated right now.
            </h3>
            <p className="mx-auto mt-3 max-w-md text-sm text-black/70 leading-relaxed">
              New projects are added here as we deliver them. In the meantime, tell us what
              you&rsquo;re planning — we&rsquo;d love to make it part of this wall.
            </p>
            <a
              href="#enquiry"
              data-testid="showcase-empty-cta"
              className="group mt-8 inline-flex items-center gap-2.5 rounded-full bg-gold px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-gold-dark hover:shadow-[0_8px_32px_rgba(224,19,39,0.4)]"
            >
              Start a project
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </motion.div>
        )}

        {items !== null && items.length > 0 && (
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="showcase-grid">
            {items.map((item, i) => (
              <motion.article
                key={item.id}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`showcase-card-${i + 1}`}
                className="group relative overflow-hidden rounded-3xl border border-black/10 bg-white transition-all duration-300 hover:border-gold/60 hover:shadow-[0_18px_50px_rgba(224,19,39,0.14)]"
              >
                <div className="relative overflow-hidden aspect-[4/3]">
                  <img
                    src={`${API}/gallery/image/${item.id}`}
                    alt={item.title}
                    data-testid={`showcase-image-${i + 1}`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-royal-deep/60 via-transparent to-transparent" />
                </div>
                <div className="relative p-6">
                  {item.category && (
                    <span className="inline-block rounded-full border border-gold/40 bg-gold-soft px-3 py-1 font-mono text-[10px] tracking-[0.2em] uppercase text-gold-dark">
                      {item.category}
                    </span>
                  )}
                  <h3 className="mt-3 font-display text-lg font-semibold tracking-tight text-royal-deep leading-snug">
                    {item.title}
                  </h3>
                  {item.description && (
                    <p className="mt-2 text-sm text-black/65 leading-relaxed line-clamp-3">
                      {item.description}
                    </p>
                  )}
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
